import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";

const norm = (s) => String(s ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const usePaged = (items, { size = 15, query = "", fields = [], key = "" } = {}) => {
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const q = norm(query.trim());
    return q ? items.filter((it) => fields.some((f) => norm(typeof f === "function" ? f(it) : it[f]).includes(q))) : items;
  }, [items, query]); // eslint-disable-line react-hooks/exhaustive-deps
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  useEffect(() => { setPage(1); }, [query, key]);
  const cur = Math.min(page, pages);
  return { rows: filtered.slice((cur - 1) * size, cur * size), page: cur, pages, setPage, total: filtered.length };
};

const nums = (page, pages) => {
  const s = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
  const out = [];
  [...s].sort((a, b) => a - b).forEach((n, i, arr) => { if (i && n - arr[i - 1] > 1) out.push("…" + n); out.push(n); });
  return out;
};

export const Pager = ({ page, pages, setPage, total, id = "list" }) => pages <= 1 ? null : (
  <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-testid={`${id}-pager`}>
    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{total} results · page {page}/{pages}</span>
    <div className="flex items-center gap-1">
      <button disabled={page === 1} onClick={() => setPage(page - 1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-[#FF2E63] disabled:opacity-30" data-testid={`${id}-prev`}><ChevronLeft className="h-4 w-4" /></button>
      {nums(page, pages).map((n) => typeof n === "string" ? <span key={n} className="px-1 text-slate-600">…</span> : (
        <button key={n} onClick={() => setPage(n)} data-testid={`${id}-page-${n}`}
          className={`h-9 min-w-9 rounded-full px-3 font-mono text-xs transition-colors ${n === page ? "bg-[#FF2E63] text-white" : "border border-white/15 text-slate-400 hover:border-white/40"}`}>{n}</button>
      ))}
      <button disabled={page === pages} onClick={() => setPage(page + 1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-[#FF2E63] disabled:opacity-30" data-testid={`${id}-next`}><ChevronRight className="h-4 w-4" /></button>
    </div>
  </div>
);

export const SearchBar = ({ value, set, placeholder = "Search…", id = "list" }) => (
  <div className="relative mb-4 w-full max-w-md">
    <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
    <input value={value} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={`${id}-search`}
      className="h-11 w-full rounded-full border border-white/10 bg-[#07090E] pl-10 pr-10 text-sm outline-none transition-colors focus:border-[#FF2E63]" />
    {value && <button onClick={() => set("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid={`${id}-search-clear`}><X className="h-4 w-4" /></button>}
  </div>
);
